/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import validate from 'validate.js';
import { withRouter } from 'react-router-dom';
import { Grid } from '@mui/material';
import moment from 'moment';

import {
  InputV2,
  DialogDefaultV2,
  DefaultSelectV2,
  UsuarioSelectV2,
  DatePickerV2,
  ArquivoContainerV2,
  TabFormV2,
} from '../../../components/v2';
import * as selectorsCartoes from '../../../store/cartao/reducer';
import * as actionsCartoes from '../../../store/cartao/actions';
import * as selectorsAuth from '../../../store/auth/reducer';
import { maskCardNumber } from '../../../utils/maskCardNumber';
import {
  normalizeCartaoStatus,
  statusCartaoOptions,
  tipoCartaoOptions,
} from './constants';
import NotasTab from './NotasTab';

const baseSchema = {
  colaboradorId: {
    presence: { allowEmpty: false, message: 'é obrigatório' },
  },
  tipo: {
    presence: { allowEmpty: false, message: 'é obrigatório' },
  },
  codigo: {
    presence: { allowEmpty: false, message: 'é obrigatório' },
  },
  status: {
    presence: { allowEmpty: false, message: 'é obrigatório' },
  },
};

const getValidationSchema = (status) => {
  if (status && status !== 'ATIVO') {
    return {
      ...baseSchema,
      dataCancelamento: {
        presence: { allowEmpty: false, message: 'é obrigatória' },
      },
    };
  }
  return baseSchema;
};

const getDefaultValues = (authData) => ({
  criadoPor: authData.id,
  criadoPorName: authData.nome,
  dataInclusao: moment().format('DD/MM/YYYY HH:mm:ss'),
  status: 'ATIVO',
});

const normalizeCartaoValues = (cartao) => ({
  ...cartao,
  colaboradorId: cartao.colaboradorId || null,
  colaboradorAtualId: cartao.colaboradorAtualId || null,
  observacao: cartao.observacao ?? cartao.descricao ?? '',
  status: normalizeCartaoStatus(cartao) || 'ATIVO',
  dataCancelamento: cartao.dataCancelamento
    ? moment(cartao.dataCancelamento).valueOf()
    : null,
});

const buildSavePayload = (values) => ({
  ...values,
  observacao: values.observacao || '',
  descricao: values.observacao || '',
  ativo: values.status === 'ATIVO',
  dataCancelamento: values.dataCancelamento
    ? moment(Number(values.dataCancelamento)).format('YYYY-MM-DD')
    : null,
});

const Formulario = ({
  saveCartao,
  updateCartao,
  selectedCartao,
  open,
  id,
  onClose,
  findCartaoById,
  authData,
  hasPermission,
}) => {
  const [values, setValues] = useState(getDefaultValues(authData));
  const [errors, setErrors] = useState({});
  const [currentTab, setCurrentTab] = useState(0);

  useEffect(() => {
    if (open && !id) {
      setValues(getDefaultValues(authData));
      setCurrentTab(0);
    }
  }, [open, id, authData]);

  useEffect(() => {
    if (id) {
      findCartaoById(id);
    }
  }, [id, findCartaoById]);

  useEffect(() => {
    if (id && selectedCartao && id === selectedCartao.id) {
      setValues(normalizeCartaoValues(selectedCartao));
    }
  }, [id, selectedCartao]);

  const onSave = () => {
    const schema = getValidationSchema(values.status);
    const erros = validate(values, schema);
    setErrors(erros || {});

    if (!erros) {
      const payload = buildSavePayload(values);
      if (values.id) {
        updateCartao(payload, saveCallback);
      } else {
        saveCartao(payload, saveCallback);
      }
    }
  };

  const saveCallback = (success) => {
    if (success) {
      onCloseLocal(true);
    }
  };

  const onCloseLocal = (isSave) => {
    setErrors({});
    setValues(getDefaultValues(authData));
    setCurrentTab(0);
    onClose(isSave);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((current) => {
      const next = { ...current, [name]: value };
      if (name === 'status' && value === 'ATIVO') {
        next.dataCancelamento = null;
      }
      return next;
    });
  };

  const dadosForm = (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <InputV2
          error={!!(errors && errors.codigo)}
          helperText={errors?.codigo?.[0]}
          isRequired
          label="Número do Cartão"
          name="codigo"
          onChange={handleChange}
          value={
            hasPermission
              ? values.codigo || ''
              : maskCardNumber(values.codigo, false)
          }
          disabled={!hasPermission}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <DefaultSelectV2
          error={!!(errors && errors.tipo)}
          isRequired
          label="Tipo de Cartão"
          name="tipo"
          showEmpty
          options={tipoCartaoOptions}
          optionLabel="nome"
          onSelect={handleChange}
          value={values.tipo || ''}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <UsuarioSelectV2
          ativo
          error={!!(errors && errors.colaboradorId)}
          isRequired
          label="Colaborador Titular"
          onSelect={(colaboradorId) => setValues({ ...values, colaboradorId })}
          value={values.colaboradorId}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <UsuarioSelectV2
          ativo
          label="Colaborador com o Cartão"
          onSelect={(colaboradorAtualId) =>
            setValues({ ...values, colaboradorAtualId })
          }
          value={values.colaboradorAtualId}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <DefaultSelectV2
          error={!!(errors && errors.status)}
          isRequired
          label="Status"
          name="status"
          showEmpty
          options={statusCartaoOptions}
          optionLabel="nome"
          onSelect={handleChange}
          value={values.status || ''}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <DatePickerV2
          error={!!(errors && errors.dataCancelamento)}
          isRequired={values.status !== 'ATIVO'}
          label="Data de Cancelamento"
          onChange={(dataCancelamento) =>
            setValues({ ...values, dataCancelamento })
          }
          value={values.dataCancelamento}
          disabled={values.status === 'ATIVO'}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <InputV2 label="Criado Por" disabled value={values.criadoPorName || ''} />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <InputV2 label="Data Inclusão" disabled value={values.dataInclusao || ''} />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <InputV2
          label="Observações"
          name="observacao"
          multiline
          rows={3}
          onChange={handleChange}
          value={values.observacao || ''}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <ArquivoContainerV2
          arquivos={values.arquivos}
          onSave={(arquivos) => setValues({ ...values, arquivos })}
        />
      </Grid>
    </Grid>
  );

  const tabPanels = [{ label: 'Dados do Cartão', element: dadosForm }];
  if (id && hasPermission) {
    tabPanels.push({
      label: 'Notas',
      element: <NotasTab cartaoId={id} />,
    });
  }

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={() => onCloseLocal(false)}
      handlerConfirm={onSave}
      title="Cartão"
      confirmMessage="Salvar"
      maxWidth="md"
      fullWidth
      readOnly={!hasPermission}
    >
      <TabFormV2
        tabPanels={tabPanels}
        currentTab={currentTab}
        onChangeTab={setCurrentTab}
      />
    </DialogDefaultV2>
  );
};

Formulario.propTypes = {
  authData: PropTypes.object,
  findCartaoById: PropTypes.func.isRequired,
  hasPermission: PropTypes.bool,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  saveCartao: PropTypes.func.isRequired,
  selectedCartao: PropTypes.object,
  updateCartao: PropTypes.func.isRequired,
};

Formulario.defaultProps = {
  authData: {},
  hasPermission: false,
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
  selectedCartao: selectorsCartoes.getSelectedCartao(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...actionsCartoes,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Formulario));
